import React, { useCallback, useMemo } from "react";
import {
  Button,
  Input,
  Select,
  SelectOption,
  Autocomplete,
  SelectGroup,
  Grid,
  GridCell,
} from "@flixbus/honeycomb-react";
import { Icon, IconPlus } from "@flixbus/honeycomb-icons-react";
import { lineOptions } from "../../constants/lines";
import type { CalculationStatus } from "../CalculationInputList/CalculationInputList.types";
import type { CalculationInputFiltersProps } from "./CalculationInputFilters.types";
import styles from "./CalculationInputFilters.module.scss";

const currentYear = new Date().getFullYear();
const yearOptions = [
  currentYear - 1,
  currentYear,
  currentYear + 1,
  currentYear + 2,
];

const STATUS_OPTIONS: {
  id: string;
  value: CalculationStatus;
  name: string;
  label: string;
}[] = [
  { id: "status-draft", value: "DRAFT", name: "filter-status", label: "Draft" },
  {
    id: "status-complete",
    value: "COMPLETE",
    name: "filter-status",
    label: "Complete",
  },
];

const toOption = (value: string) => ({ title: value, value });
const toSupplierOption = (s: { id: string; name: string }) => ({
  title: s.name,
  value: s.id,
});
const toSelectedValues = (selected: { value: string | number }[]) =>
  selected.map((option) => String(option.value));

const CalculationInputFilters: React.FC<CalculationInputFiltersProps> = ({
  draft,
  kamOptions,
  supplierOptions,
  onSetField,
  onAdd,
  addDisabled,
  addDisabledReason,
}) => {
  const lineAutocompleteOptions = useMemo(() => lineOptions.map(toOption), []);
  const lineAutocompleteValue = useMemo(
    () => draft.lines.map(toOption),
    [draft.lines],
  );
  const supplierAutocompleteOptions = useMemo(
    () => supplierOptions.map(toSupplierOption),
    [supplierOptions],
  );
  const supplierAutocompleteValue = useMemo(
    () =>
      draft.supplierIds.map((id) => {
        const supplier = supplierOptions.find((option) => option.id === id);
        return { title: supplier?.name ?? id, value: id };
      }),
    [draft.supplierIds, supplierOptions],
  );
  const kamAutocompleteOptions = useMemo(
    () => kamOptions.map(toOption),
    [kamOptions],
  );
  const kamAutocompleteValue = useMemo(
    () => draft.kamNames.map(toOption),
    [draft.kamNames],
  );
  const tagProps = useCallback(
    (option: { title: string | number }) => ({
      closeProps: { label: `Remove ${String(option.title)}` },
    }),
    [],
  );
  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onSetField("name", event.target.value),
    [onSetField],
  );
  const handleLinesChange = useCallback(
    (selected: { value: string | number }[]) =>
      onSetField("lines", toSelectedValues(selected)),
    [onSetField],
  );
  const handleSupplierIdsChange = useCallback(
    (selected: { value: string | number }[]) =>
      onSetField("supplierIds", toSelectedValues(selected)),
    [onSetField],
  );
  const handleKamNamesChange = useCallback(
    (selected: { value: string | number }[]) =>
      onSetField("kamNames", toSelectedValues(selected)),
    [onSetField],
  );
  const handlePlanningYearChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) =>
      onSetField(
        "planningYear",
        event.target.value ? parseInt(event.target.value, 10) : null,
      ),
    [onSetField],
  );
  const resetLines = useCallback(() => onSetField("lines", []), [onSetField]);
  const resetSupplierIds = useCallback(
    () => onSetField("supplierIds", []),
    [onSetField],
  );
  const resetKamNames = useCallback(
    () => onSetField("kamNames", []),
    [onSetField],
  );
  const handleStatusClick = useCallback(
    (status: CalculationStatus) =>
      onSetField("status", draft.status === status ? null : status),
    [draft.status, onSetField],
  );
  const lineComboboxProps = useMemo(
    () => ({
      label: "Line",
      tagProps,
      resetProps: {
        label: "Reset",
        onClick: resetLines,
      },
    }),
    [resetLines, tagProps],
  );
  const supplierComboboxProps = useMemo(
    () => ({
      label: "Supplier",
      tagProps,
      resetProps: {
        label: "Reset",
        onClick: resetSupplierIds,
      },
    }),
    [resetSupplierIds, tagProps],
  );
  const kamComboboxProps = useMemo(
    () => ({
      label: "KAM",
      tagProps,
      resetProps: {
        label: "Reset",
        onClick: resetKamNames,
      },
    }),
    [resetKamNames, tagProps],
  );
  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.map((opt) => ({
        ...opt,
        checked: draft.status === opt.value,
        onChange: () => {},
        onClick: () => handleStatusClick(opt.value),
      })),
    [draft.status, handleStatusClick],
  );

  return (
    <div className={styles.filterContainer}>
      <Grid align="start" extraClasses={styles.filterGrid}>
        <GridCell colSpan={{ default: 12, md: 4, lg: 2 }}>
          <Input
            id="filter-name"
            label="Calculation name"
            value={draft.name}
            onChange={handleNameChange}
          />
        </GridCell>
        <GridCell colSpan={{ default: 12, md: 4, lg: 2 }}>
          <Autocomplete
            multiple
            options={lineAutocompleteOptions}
            value={lineAutocompleteValue}
            onChange={handleLinesChange}
            comboboxProps={lineComboboxProps}
          />
        </GridCell>
        <GridCell colSpan={{ default: 12, md: 4, lg: 2 }}>
          <Autocomplete
            multiple
            options={supplierAutocompleteOptions}
            value={supplierAutocompleteValue}
            onChange={handleSupplierIdsChange}
            comboboxProps={supplierComboboxProps}
          />
        </GridCell>
        <GridCell colSpan={{ default: 12, md: 4, lg: 2 }}>
          <Autocomplete
            multiple
            options={kamAutocompleteOptions}
            value={kamAutocompleteValue}
            onChange={handleKamNamesChange}
            comboboxProps={kamComboboxProps}
          />
        </GridCell>
        <GridCell colSpan={{ default: 12, md: 4, lg: 1 }}>
          <Select
            id="filter-year"
            label="Planning year"
            value={
              draft.planningYear !== null ? String(draft.planningYear) : ""
            }
            onChange={handlePlanningYearChange}
          >
            <SelectOption value="">{"— All —"}</SelectOption>
            {yearOptions.map((y) => (
              <SelectOption key={y} value={String(y)}>
                {y}
              </SelectOption>
            ))}
          </Select>
        </GridCell>
        <GridCell colSpan={{ default: 12, md: 4, lg: 2 }}>
          <SelectGroup
            extraClasses={styles.statusSelectGroup}
            label="Status"
            options={statusOptions}
            multi
          />
        </GridCell>
        <GridCell
          colSpan={{ default: 12, md: 4, lg: 1 }}
          extraClasses={styles.filterActionsGrid}
        >
          <Button
            display="square"
            appearance="primary"
            aria-label="Add calculation"
            title={addDisabledReason ?? undefined}
            disabled={addDisabled}
            onClick={onAdd}
          >
            <Icon InlineIcon={IconPlus} aria-hidden="true" />
          </Button>
        </GridCell>
      </Grid>
    </div>
  );
};

export default React.memo(CalculationInputFilters);
